import dayjs from "dayjs";
import type { TeleconsultoriaActor, TeleconsultoriaGuest } from "../../types";
import {
  locationParticipantActor,
  patientParticipantActor,
} from "../../utils/teleconsultoriaAppointment";
import {
  DEFAULT_INVITE_KIND,
  INVITE_APPOINTMENT_IDENTIFIER_SYSTEM,
  INVITE_FALLBACK_VALIDITY_IN_MINUTES,
  INVITE_INPUT,
  INVITE_INPUT_SYSTEM,
  INVITE_PROVIDER_IDENTIFIER_SYSTEM,
  INVITE_TASK_CODE,
  INVITE_TASK_CODE_SYSTEM,
  INVITE_TASK_CODE_TEXT,
  type InviteKind,
} from "../constants";

interface BuildInviteTaskParams {
  appointment: fhir5.Appointment;
  guest: TeleconsultoriaGuest;
  guestRoleReference: string;
  hostRoleReference: string;
  hostDisplay: string;
  /** Opcional: padrão "in-progress" (convite de atendimento aberto, como antes). */
  inviteKind?: InviteKind;
}

function patientActor(appointment: fhir5.Appointment): TeleconsultoriaActor | undefined {
  // Aceita `Patient/<id>` e `Patient?identifier=...` (formato gravado pelo AuxiliarData).
  const actor = patientParticipantActor(appointment);
  if (!actor?.reference) return undefined;
  return { reference: actor.reference, display: actor.display || "" };
}

/** Prazo do convite: fim do atendimento; se já passou, agora + margem. */
function inviteDeadline(appointment: fhir5.Appointment): string {
  const end = appointment.end ? dayjs(appointment.end) : undefined;
  if (end?.isValid() && end.isAfter(dayjs())) return end.toISOString();
  return dayjs().add(INVITE_FALLBACK_VALIDITY_IN_MINUTES, "minute").toISOString();
}

function referenceInput(code: string, text: string, actor: TeleconsultoriaActor) {
  return {
    type: { coding: [{ system: INVITE_INPUT_SYSTEM, code }], text },
    valueReference: { reference: actor.reference, display: actor.display },
  };
}

/**
 * Paciente só para exibição: texto, sem referência. O Patient fica em outro
 * servidor e pode vir como referência condicional (`Patient?identifier=...`),
 * que o HAPI do workflow tentaria resolver.
 */
function patientInput(patient: TeleconsultoriaActor) {
  return {
    type: { coding: [{ system: INVITE_INPUT_SYSTEM, code: INVITE_INPUT.patient }], text: "Paciente" },
    valueString: patient.display || "Paciente",
  };
}

function inviteDescription(
  hostDisplay: string,
  patient: string,
  start: string | undefined,
  inviteKind: InviteKind,
) {
  const when = start && dayjs(start).isValid() ? ` · ${dayjs(start).format("DD/MM HH:mm")}` : "";
  const host = hostDisplay || "Um profissional";
  // Reserva com horário: o texto deixa claro que é um pedido de agendamento.
  if (inviteKind === "scheduled") {
    return `${host} pediu uma teleconsultoria com você para ${patient}${when}`;
  }
  return `${host} convidou você para a teleconsultoria de ${patient}${when}`;
}

function inviteKindInput(inviteKind: InviteKind) {
  return {
    type: {
      coding: [{ system: INVITE_INPUT_SYSTEM, code: INVITE_INPUT.inviteKind }],
      text: "Tipo do convite",
    },
    valueCode: inviteKind,
  };
}

/**
 * Task de convite. `for`/`owner` = PractitionerRole do convidado (é quem vê a
 * notificação); `requester` = PractitionerRole do host.
 * O Appointment de origem vai SÓ como `identifier` (id lógico), nunca como
 * referência: ele fica em outro servidor e o HAPI do workflow responderia 500.
 * `location` = Location do atendimento de origem (mesmo servidor do workflow):
 * o convidado precisa dela para buscar o Appointment no scheduling.
 * O Appointment NÃO é alterado aqui: o convidado entra nele só ao aceitar.
 */
export function buildInviteTask({
  appointment,
  guest,
  guestRoleReference,
  hostRoleReference,
  hostDisplay,
  inviteKind = DEFAULT_INVITE_KIND,
}: BuildInviteTaskParams): fhir5.Task {
  const now = dayjs().toISOString();
  const patient = patientActor(appointment);
  const originLocation = locationParticipantActor(appointment);
  const guestRole = { reference: guestRoleReference, display: guest.practitioner.display };

  return {
    resourceType: "Task",
    identifier: [
      // Appointment de origem (outro servidor): só o id, pesquisável por identifier.
      { system: INVITE_APPOINTMENT_IDENTIFIER_SYSTEM, value: appointment.id },
      // Vínculo pesquisável com a prestadora (organização do convidado).
      ...(guest.organizationId
        ? [{ system: INVITE_PROVIDER_IDENTIFIER_SYSTEM, value: guest.organizationId }]
        : []),
    ],
    status: "requested",
    intent: "order",
    priority: "routine",
    code: {
      coding: [{ system: INVITE_TASK_CODE_SYSTEM, code: INVITE_TASK_CODE }],
      text: INVITE_TASK_CODE_TEXT,
    },
    description: inviteDescription(
      hostDisplay,
      patient?.display || "um paciente",
      appointment.start,
      inviteKind,
    ),
    // Location de onde veio o convite: filtro obrigatório na busca do Appointment.
    ...(originLocation?.reference && {
      location: { reference: originLocation.reference, display: originLocation.display },
    }),
    for: guestRole,
    owner: guestRole,
    requester: { reference: hostRoleReference, display: hostDisplay },
    authoredOn: now,
    lastModified: now,
    restriction: { period: { end: inviteDeadline(appointment) } },
    input: [
      referenceInput(INVITE_INPUT.guestPractitioner, "Teleconsultor", guest.practitioner),
      ...(guest.healthcareService
        ? [referenceInput(INVITE_INPUT.guestHealthcareService, "Serviço", guest.healthcareService)]
        : []),
      ...(patient ? [patientInput(patient)] : []),
      inviteKindInput(inviteKind),
    ],
  };
}
